import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { queryClient } from '~/common/react-query/queryClient'
import fetch from '~/common/fetch'
import { DateString } from '~/common/types'

export type HomeConfig = {
  contact_us: {
    enabled: boolean
    custom_message: string | null
    hide_form: boolean
  }
  dashboards: {
    enabled: boolean
    can_access_dashboard: boolean
  }
  reports: {
    can_access_custom_reports: boolean
    can_access_favorite_reports: boolean
  }
  bulkupdates: {
    can_access_bulkupdates: boolean
    support_wt: boolean
    support_wo_cont: boolean
    support_wo_sow: boolean
    support_candidate: boolean
    support_job: boolean
  }
  tasks: {
    can_dismiss_all: boolean
  }
  set_event_tab_as_default: boolean
  home_page_gradient_enabled: boolean
}

export enum QuickActionPlacement {
  PLACEMENT_NONE = 0,
  PLACEMENT_HOME_SCREEN = 1
}

export type QuickAction = {
  id: string
  url: string
  title: string
  count?: string
  placement?: QuickActionPlacement
}

export type QuickActionsResponse = Array<QuickAction>

export type ProgramHealthStat = {
  id: string
  title: string
  content: string
  url?: string
  sub_header1?: string
  sub_content1?: string
  sub_url1?: string
  sub_header2?: string
  sub_content2?: string
  sub_url2?: string
}

export type ProgramHealthStatsResponse = { header_items: Array<ProgramHealthStat> }

export enum HomeEventStatus {
  UNDECIDED = '1',
  ACCEPTED = '2',
  DECLINED = '3',
  CANCELLED = '4'
}

export type HomeEvent = {
  id: string
  status: HomeEventStatus
  title: string
  start: DateString
}

export type HomeEventsResponse = {
  todays_events: Array<HomeEvent>
  todays_events_count: number
  upcoming_events: Array<HomeEvent>
  upcoming_count: number
}

export type HomeMessage = {
  id: string
  cfrom: { first_name: string }
  is_read: boolean
  cto: { first_name: string }
  created_at: DateString
  message: string
}

export type HomeMessagesResponse = {
  conv: Array<HomeMessage>
  message_count: number
  is_in_app_messaging_disabled: boolean
}

export type ContactUsData = {
  name: string
  email: string
  query: string
}

export type EventRespondData = {
  id: string
  status: HomeEventStatus
  declined_reason?: string
  bridge_pin?: string
  isd_code?: string
}

export type ContactUsConfig = {
  enabled: boolean
  custom_message: string | null
  hide_form: boolean
  request_user_info: boolean
}

export const homeApi = {
  config: (): Promise<HomeConfig> => fetch.get(`/api/home/config/`),
  quickActions: (): Promise<QuickActionsResponse> => fetch.get(`/api/home/quick_actions/`),
  termsAccepted: (next_url: string): Promise<void> => fetch.post(`/api/home/terms_of_conditions/`, { next_url }),
  stats: (): Promise<ProgramHealthStatsResponse> => fetch.get(`/api/home/program_health_stats/`),
  events: (): Promise<HomeEventsResponse> => fetch.get('/api/home/events/'),
  messages: (): Promise<HomeMessagesResponse> => fetch.get('/api/home/messages/'),
  contactUs: (request: ContactUsData): Promise<{ status: 'success' | 'error'; message: string }> =>
    fetch.post(`/api/user-query/`, request),
  eventRespond: ({ id, status }: EventRespondData): Promise<void> =>
    fetch.post(`/api/respond_interview/${id}/`, { status }),
  contactUsConfig: (): Promise<ContactUsConfig> => fetch.get(`/api/contact-us-config/`)
}

export const HOME_STATS_QUERY_KEY = ['home', 'stats']
export const HOME_MESSAGES_QUERY_KEY = ['home', 'messages']
export const HOME_EVENTS_QUERY_KEY = ['home', 'events']
export const HOME_QUICK_ACTIONS_QUERY_KEY = ['home', 'quick_actions']

export const useHomeConfigQuery = () =>
  useQuery({ queryKey: ['home', 'config'], queryFn: homeApi.config, staleTime: 1000 * 60 * 60 })

export const useHomeQuickActionsQuery = () =>
  useQuery({ queryKey: HOME_QUICK_ACTIONS_QUERY_KEY, queryFn: homeApi.quickActions })

export const useTermsAndConditionsAcceptedQuery = (next_url: string, options: UseQueryOptions) => {
  // We aren't returning on purpose becasue this will just redirect via a 403 error if needed.
  useQuery({
    queryKey: ['home', 'terms'],
    queryFn: () => homeApi.termsAccepted(encodeURIComponent(next_url)),
    staleTime: 1000 * 60 * 60,
    ...options
  })
}

export const useHomeStatsQuery = () => useQuery({ queryKey: HOME_STATS_QUERY_KEY, queryFn: homeApi.stats })
export const useHomeEventsQuery = () => useQuery({ queryKey: HOME_EVENTS_QUERY_KEY, queryFn: homeApi.events })
export const useHomeMessagesQuery = () => useQuery({ queryKey: HOME_MESSAGES_QUERY_KEY, queryFn: homeApi.messages })

export const useContactUsMutation = () => useMutation({ mutationFn: homeApi.contactUs })
export const useEventRespondMutation = () =>
  useMutation({
    mutationFn: homeApi.eventRespond,
    onSuccess: () => {
      queryClient.invalidateQueries(HOME_EVENTS_QUERY_KEY)
    }
  })
export const useContactUsConfigQuery = () =>
  useQuery({ queryKey: ['contact-us-config'], queryFn: homeApi.contactUsConfig })
